import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import SettingsIcon from "@mui/icons-material/Settings";

import LanguageSettingsTab from "../components/LanguageSettingsTab";
import RolesPermissionsTab from "../components/RolesPermissionsTab";
import ApprovalSettings from "../components/ApprovalSettings";
import FeatureSettings from "../components/FeatureSetting";
import AppHomeSettings from "../components/AppHomeSettings";
import AppBottomSettings from "../components/AppBottomSettings";
import YoutubeSettings from "../components/YoutubeSettings";
import FaithStatementSettings from "../components/FaithStatementSettings";
import MyDailyDevotionSettings from "../components/MyDailyDevotionSettings";
import PageLoader from "../components/PageLoader";

import { useUser } from "../context/UserContext";
import { useLanguage } from "../context/LanguageContext";

const Settings = () => {
  const { t } = useLanguage();
  const [currentTab, setCurrentTab] = useState(() => {
    const savedTab = localStorage.getItem("settings_active_tab");
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  const [Approvals, setApprovals] = useState({});
  const [mySettings, setmySettings] = useState([]);
  const [biblestudy, setbiblestudy] = useState([]);
  const [worship, setworship] = useState([]);
  const [systemLanguages, setSystemLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("settings_active_tab", currentTab);
  }, [currentTab]);

  const { user, effectiveOrgId, orgViewMode } = useUser();
  const defaultOrgId = effectiveOrgId || orgViewMode?.orgId || (user?.org_id ? Number(user.org_id) : 1);

  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(defaultOrgId);

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  const tabs = [
    t("tab_languages", "Languages"),
    t("tab_roles_permissions", "Roles & Permissions"),
    t("tab_approval_settings", "Approval Settings"),
    t("tab_feature_settings", "Feature Settings"),
    t("tab_app_home_settings", "APP Home Page Settings"),
    t("tab_app_bottom_bar", "App Bottom Bar Settings"),
    t("tab_sunday_worship", "Sunday Worship & Bible Study"),
    t("tab_faith_statement", "Faith Statement Settings"),
    t("tab_my_daily_devotion", "⚙️ My Daily Devotion"),
  ];

  // Safety fallback: Never keep screen blank/frozen on loader
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Sync selectedOrgId when user/orgViewMode updates
  useEffect(() => {
    if (user?.role && user.role !== 1 && user.org_id) {
      setSelectedOrgId(Number(user.org_id));
    } else if (effectiveOrgId) {
      setSelectedOrgId(Number(effectiveOrgId));
    }
  }, [user, effectiveOrgId]);

  // SUPER ADMIN → Load organizations
  useEffect(() => {
    const userInfo = user || JSON.parse(localStorage.getItem("userInfo") || "{}");
    if (userInfo?.role && userInfo.role !== 1) return;

    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await fetch(`${API_URL}roles-organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        const orgList = data.organizations || data.organisations || [];
        setOrganizations(orgList);

        if (orgList.length > 0) {
          const matched = orgList.find(o => Number(o.id) === Number(selectedOrgId)) || orgList[0];
          setOrganization(matched);
          if (!selectedOrgId) {
            setSelectedOrgId(matched.id);
          }
        }
      } catch (err) {
        console.error("Error fetching organizations in settings:", err);
      }
    };

    fetchOrganizations();
  }, [user]);

  // Fetch settings when org changes
  useEffect(() => {
    const targetOrg = selectedOrgId || defaultOrgId || 1;
    fetchSettings(targetOrg);
  }, [selectedOrgId]);

  const fetchSettings = async (organizationId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const targetOrg = Number(organizationId || defaultOrgId || 1);

      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}settings`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ organization_id: targetOrg }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === "Invalid token") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
      }

      setApprovals(data.approvalSettings || {});
      setmySettings(data.settings || []);
      setbiblestudy(data.bibleStudies || []);
      setworship(data.sundayWorships || []);
      setSystemLanguages(data.languages || []);
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading && !Approvals && !mySettings.length) return <PageLoader open={true} />;

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Helmet>
        <title>Settings - GPN</title>
      </Helmet>

      {/* Header Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)",
          borderRadius: 3,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: "white",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: "rgba(76, 161, 175, 0.2)",
              border: "1px solid rgba(76, 161, 175, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4CA1AF",
            }}
          >
            <SettingsIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
              {t("settings_title", "Settings & Configurations")}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              {t("settings_subtitle", "Configure organization defaults, languages, roles & permissions, module approvals, and mobile app layouts.")}
            </Typography>
          </Box>
        </Box>

        {/* Super Admin Org Filter */}
        {user?.role === 1 && (
          <Box sx={{ mt: 2.5 }}>
            <Autocomplete
              size="small"
              value={organization}
              onChange={(e, newValue) => {
                setOrganization(newValue);
                setSelectedOrgId(newValue?.id);
              }}
              options={organizations}
              getOptionLabel={(opt) => opt?.org_name || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("select_organization_to_configure", "Select Organization to Configure")}
                  InputLabelProps={{ sx: { color: "rgba(255, 255, 255, 0.7)" } }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      color: "#FFFFFF",
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    },
                  }}
                />
              )}
              sx={{ width: { xs: "100%", sm: 340 } }}
            />
          </Box>
        )}

        {/* Navigation Tabs Bar */}
        <Paper
          elevation={0}
          sx={{
            mt: 2.5,
            background: "rgba(255, 255, 255, 0.07)",
            backdropFilter: "blur(10px)",
            borderRadius: 2,
            p: 0.5,
            border: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": {
                backgroundColor: "#4CA1AF",
                height: 3,
                borderRadius: 2,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "rgba(255, 255, 255, 0.7)",
                minHeight: 44,
                px: 2,
                borderRadius: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#ffffff",
                  background: "rgba(76, 161, 175, 0.15)",
                },
                "&.Mui-selected": {
                  color: "#4CA1AF",
                  background: "rgba(76, 161, 175, 0.2)",
                  fontWeight: 700,
                },
              },
            }}
          >
            {tabs.map((tab, i) => (
              <Tab key={i} label={tab} />
            ))}
          </Tabs>
        </Paper>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ mt: 2 }}>
        {currentTab === 0 && (
          <LanguageSettingsTab
            approvals={Approvals}
            organization={selectedOrgId}
            languages={systemLanguages}
          />
        )}
        {currentTab === 1 && (
          <RolesPermissionsTab />
        )}
        {currentTab === 2 && (
          <ApprovalSettings approvals={Approvals} organization={selectedOrgId} />
        )}
        {currentTab === 3 && (
          <FeatureSettings mySettings={mySettings} organization={selectedOrgId} />
        )}
        {currentTab === 4 && (
          <AppHomeSettings mySettings={mySettings} organization={selectedOrgId} />
        )}
        {currentTab === 5 && (
          <AppBottomSettings mySettings={mySettings} organization={selectedOrgId} />
        )}
        {currentTab === 6 && (
          <YoutubeSettings
            biblestudy={biblestudy}
            worship={worship}
            organization={selectedOrgId}
          />
        )}
        {currentTab === 7 && (
          <FaithStatementSettings
            mySettings={mySettings}
            organization={selectedOrgId}
          />
        )}
        {currentTab === 8 && (
          <MyDailyDevotionSettings
            organization={selectedOrgId}
          />
        )}
      </Box>
    </Box>
  );
};

export default Settings;
